<template>
  <div class="hall-effect-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$U_H = R_H · (I·B / d)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>磁感应强度 B</label>
            <span class="val">{{ fieldB.toFixed(1) }} <small>T</small></span>
          </div>
          <div class="hud-item orange">
            <label>霍尔电压 U_H</label>
            <span class="val">{{ (hallVoltage * 10).toFixed(2) }} <small>mV</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <filter id="glow-carrier"><feGaussianBlur stdDeviation="2" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <marker id="arr-hall" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
             </marker>
          </defs>

          <!-- 外部垂直磁场线 (B) -->
          <g v-if="Math.abs(fieldB) > 0.1">
             <line v-for="i in 6" :key="'b'+i" 
                   :x1="60 + i*40" y1="30" :x2="60 + i*40" y2="210" 
                   stroke="#38bdf8" stroke-width="1.5" opacity="0.4" :stroke-dasharray="fieldB > 0 ? '' : '5,3'"
                   :marker-end="fieldB > 0 ? 'url(#arr-hall)' : ''" />
             <text x="350" y="45" fill="#38bdf8" font-size="10" font-weight="bold">B-Field</text>
          </g>

          <!-- 霍尔元件 (导体/半导体) -->
          <rect x="100" y="90" width="200" height="70" fill="rgba(30, 41, 59, 0.8)" stroke="#475569" stroke-width="2" rx="4" />
          
          <!-- 主电流 I -->
          <line x1="40" y1="125" x2="90" y2="125" stroke="#f43f5e" stroke-width="3" marker-end="url(#arr-hall)" />
          <line x1="310" y1="125" x2="360" y2="125" stroke="#f43f5e" stroke-width="3" />
          <text x="50" y="115" fill="#f43f5e" font-size="10" font-weight="900">Current I</text>

          <!-- 载流子运动 (Lorentz force deflection) -->
          <g v-for="e in carriers" :key="e.id">
             <circle :cx="e.pos" :cy="125 + e.offset" r="3" :fill="carrierColor" filter="url(#glow-carrier)">
                <animate attributeName="opacity" values="1;0.4;1" dur="0.5s" repeatCount="indefinite" />
             </circle>
          </g>

          <!-- 电荷堆积指示 -->
          <g v-if="Math.abs(fieldB) > 0.5">
             <!-- Top Edge Charges -->
             <text v-for="i in 6" :key="'tc'+i" :x="115 + i*25" :y="85" :fill="topChargeColor" font-size="12" font-weight="900" text-anchor="middle">
                {{ topChargeSign }}
             </text>
             <!-- Bottom Edge Charges -->
             <text v-for="i in 6" :key="'bc'+i" :x="115 + i*25" :y="178" :fill="bottomChargeColor" font-size="12" font-weight="900" text-anchor="middle">
                {{ bottomChargeSign }}
             </text>
          </g>

          <!-- 霍尔电压表连接 -->
          <path d="M 200,90 V 60 H 180" fill="none" stroke="#64748b" stroke-width="1.5" />
          <path d="M 200,160 V 190 H 180" fill="none" stroke="#64748b" stroke-width="1.5" />
          <rect x="140" y="50" width="40" height="20" rx="3" fill="#0f172a" stroke="#334155" />
          <text x="160" y="64" text-anchor="middle" fill="#fbbf24" font-size="10" font-weight="bold">V_H</text>
        </svg>

        <div class="interaction-hint">切换载流子类型观察电势差 U_H 的极性反转</div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center gap-4">
                 <span class="label" style="width: 100px">调节磁场强度 B</span>
                 <el-slider v-model="fieldB" :min="-10" :max="10" :step="0.5" size="small" class="flex-1"></el-slider>
              </div>
              <div class="flex items-center justify-between">
                 <span class="label">选择载流子 (Carrier Type)</span>
                 <el-radio-group v-model="carrierType" size="mini">
                    <el-radio-button label="n">N型 (电子 -)</el-radio-button>
                    <el-radio-button label="p">P型 (空穴 +)</el-radio-button>
                 </el-radio-group>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>载流子在磁场中受到<strong>洛伦兹力</strong>。如果是电子(n型)，向下偏转使底部带负电；如果是空穴(p型)，向下偏转使底部带正电。通过测量霍尔电压的正负，我们能判定材料是N型还是P型，这是现代传感器技术的物理基石。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HallEffectViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      fieldB: 5,
      carrierType: 'n',
      t: 0,
      carriers: Array.from({ length: 12 }, (_, i) => ({
         id: i,
         pos: 100 + Math.random() * 200,
         seed: Math.random()
      }))
    }
  },
  computed: {
    hallVoltage() { return this.fieldB * (this.carrierType === 'n' ? -1 : 1); },
    carrierColor() { return this.carrierType === 'n' ? '#10b981' : '#f43f5e'; },
    topChargeSign() { return this.hallVoltage > 0 ? '-' : '+'; },
    bottomChargeSign() { return this.hallVoltage > 0 ? '+' : '-'; },
    topChargeColor() { return this.hallVoltage > 0 ? '#10b981' : '#f43f5e'; },
    bottomChargeColor() { return this.hallVoltage > 0 ? '#f43f5e' : '#10b981'; }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); },
  methods: {
    animate() {
       this.t += 0.02;
       this.carriers.forEach(c => {
          c.pos += (1.5 + c.seed);
          if (c.pos > 300) c.pos = 100;
          // Dynamically calculate offset based on current fieldB and carrierType
          const forceDir = this.carrierType === 'n' ? 1 : -1;
          c.offset = this.fieldB * forceDir * 2.5; 
       });
       this.animFrame = requestAnimationFrame(this.animate);
    }
  }
}
</script>

<style scoped lang="scss">
.hall-effect-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f1f5f9; color: #334155; border: 1px solid #e2e8f0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #fbbf24; }
    }
  }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .flex-1 { flex: 1; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }
.val-tag { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; }

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #64748b; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
